import type { AddOn } from '../features/addOns/addOnsSlice';

/**
 * The server prices a customer's booking from its own price list and adds
 * the fee lines the portal always adds (AppointmentService). When that comes
 * to more than the basket showed it refuses the booking with
 * BOOKING_PRICE_CHANGED and every line it would book, which the Schedule
 * step puts in the basket so the customer sees the real total before booking
 * again.
 */
export const BOOKING_PRICE_CHANGED = 'BOOKING_PRICE_CHANGED';

/** One booked line as the server charges it. */
export interface ServerLinePrice {
  /** The service id (the basket item's id). */
  id: number;
  /** The price, in cents. */
  cents: number;
  /** The subscription-discounted price, in cents, or null when none. */
  discountedCents: number | null;
  name: string | null;
  nameKey: string | null;
  serviceTypeName: string | null;
}

const finite = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);
const text = (value: unknown): string | null => (
  typeof value === 'string' ? value : null
);

/** The lines a BOOKING_PRICE_CHANGED refusal carries; [] otherwise. */
export function serverLinePrices(
  response?: { [key: string]: unknown } | null,
): ServerLinePrice[] {
  const lines = response?.lineItemPrices;
  if (!Array.isArray(lines)) return [];
  return (lines as Array<Record<string, unknown> | null>)
    .filter((line) => finite(line?.id) && finite(line?.cents))
    .map((line) => line as Record<string, unknown>)
    .map((line) => ({
      id: line.id as number,
      cents: line.cents as number,
      discountedCents: finite(line.discountedCents)
        ? line.discountedCents
        : null,
      name: text(line.name),
      nameKey: text(line.nameKey),
      serviceTypeName: text(line.serviceTypeName),
    }));
}

/**
 * Basket items with the server's prices, in place (Redux Toolkit drafts):
 * each item the server priced gets its price (dollars, as the basket keeps
 * it) and its discounted price, or none. Items it did not price are left as
 * they are.
 */
export function applyServerLinePrices<
  T extends { id: number; value: number; discountedValue?: number },
>(items: T[], prices: ServerLinePrice[]): void {
  const byId = new Map(prices.map((line) => [line.id, line]));
  items.forEach((item) => {
    const line = byId.get(item.id);
    if (!line) return;
    // eslint-disable-next-line no-param-reassign
    item.value = line.cents / 100;
    // eslint-disable-next-line no-param-reassign
    item.discountedValue = line.discountedCents === null
      ? undefined
      : line.discountedCents / 100;
  });
}

/**
 * What the basket must change so it holds exactly the lines the server would
 * book: the add-ons it has that the server does not book (a fee row the
 * basket does not call for), and the lines the server adds (a fee the basket
 * left out), as add-ons priced by the server. A line the server adds is taken
 * from `catalog` (the fee list the Schedule step loaded) when it is there.
 */
export function basketChanges(
  prices: ServerLinePrice[],
  basket: { services: { id: number }[]; addOns: { id: number }[] },
  catalog: AddOn[] = [],
): { remove: number[]; add: AddOn[] } {
  if (!prices.length) return { remove: [], add: [] };
  const priced = new Set(prices.map((line) => line.id));
  const held = new Set(
    [...basket.services, ...basket.addOns].map((item) => item.id),
  );
  const remove = basket.addOns
    .map((item) => item.id)
    .filter((id) => !priced.has(id));
  const add = prices
    .filter((line) => !held.has(line.id))
    .map((line): AddOn => {
      const known = catalog.find((item) => item.id === line.id);
      const discountedValue = line.discountedCents === null
        ? undefined
        : line.discountedCents / 100;
      if (known) {
        return { ...known, value: line.cents / 100, discountedValue };
      }
      return {
        id: line.id,
        name: line.name ?? '',
        nameKey: line.nameKey,
        value: line.cents / 100,
        discountedValue,
        isEstimate: false,
        description: '',
        subDescription: null,
        sortOrder: null,
        taxName: '--',
        taxPercent: 1,
        taxId: 0,
        serviceTypeId: 0,
        serviceTypeName: line.serviceTypeName ?? '',
      };
    });
  return { remove, add };
}
